import React, { useEffect, useMemo, useState } from 'react';
import { Chip, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

function formatRemaining(totalSeconds) {
    if (totalSeconds == null || Number.isNaN(totalSeconds)) return '—';
    if (totalSeconds <= 0) return 'Vencido';

    const s = Math.floor(totalSeconds);
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
}

/**
 * Chip de cuenta regresiva.
 * @param {string} [label]
 * @param {string|Date} [endsAt] — ISO o Date
 * @param {number} [remainingSeconds] — si viene del API, se usa como base
 * @param {number} [totalSeconds] — plazo total para calcular % restante
 * @param {boolean} [live=true] — actualiza cada segundo
 */
export default function CountdownChip({
    label = 'Plazo',
    endsAt = null,
    remainingSeconds = null,
    totalSeconds = null,
    live = true,
}) {
    const initialRemaining = useMemo(() => {
        if (remainingSeconds != null) return Math.max(0, Number(remainingSeconds));
        if (!endsAt) return null;
        const end = new Date(endsAt).getTime();
        if (Number.isNaN(end)) return null;
        return Math.max(0, Math.floor((end - Date.now()) / 1000));
    }, [endsAt, remainingSeconds]);

    const [left, setLeft] = useState(initialRemaining);

    useEffect(() => {
        setLeft(initialRemaining);
    }, [initialRemaining]);

    useEffect(() => {
        if (!live || left == null) return undefined;
        const id = window.setInterval(() => {
            if (endsAt) {
                const end = new Date(endsAt).getTime();
                setLeft(Math.max(0, Math.floor((end - Date.now()) / 1000)));
            } else {
                setLeft((prev) => (prev == null ? null : Math.max(0, prev - 1)));
            }
        }, 1000);
        return () => window.clearInterval(id);
    }, [live, endsAt, left == null]);

    if (left == null) return null;

    const pct = totalSeconds && totalSeconds > 0 ? left / totalSeconds : null;
    const urgent = left <= 0 || (pct != null && pct < 0.25);
    const text = `${label}: ${formatRemaining(left)}`;

    return (
        <Tooltip title={endsAt ? `Vence: ${new Date(endsAt).toLocaleString('es-AR')}` : text}>
            <Chip
                size="small"
                icon={urgent ? <WarningAmberIcon /> : <AccessTimeIcon />}
                label={text}
                color={urgent ? 'error' : 'default'}
                variant={urgent ? 'filled' : 'outlined'}
                sx={{ fontWeight: urgent ? 700 : 500 }}
            />
        </Tooltip>
    );
}
